import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import RecordCashSheet from '../../components/membership/RecordCashSheet';
import { DSCard, DSButton, DSAlert } from '../../design-system/primitives';
import { buildChartSeries } from './chartSeries';
import { summarisePayments } from './paymentsSummary';
import './membership.css';

// "See the money", which is the fourth job an admin does here and the only one
// that is a report rather than a decision.
//
// The method breakdown is the part that did not exist before. "How many people
// pay by invoice?" was previously unanswerable without opening Square, and it is
// the number that tells you how exposed the club is to the payment method the
// software understands least.

// Sponsored is deliberately absent as a category. It is a relationship, not a
// way money reaches the club, and who pays for whom belongs on the two members'
// own records - so a sponsored membership is counted under the method their
// sponsor pays by, and a footnote below says how many are folded in that way.
//
// No-method-recorded is absent for the same reason as before: a blank method is
// not work, it is a gap that closes itself when evidence arrives. `summary`
// still counts both, so the arithmetic can be checked in a test even though the
// screen does not spell it out.
const METHOD_ORDER = [
  { key: 'SUBSCRIPTION', label: 'pay by subscription' },
  { key: 'INVOICE', label: 'pay by invoice' },
  { key: 'CASH', label: 'pay cash at the club' },
  { key: 'COMP', label: 'are complimentary' }
];

function MembershipPayments() {
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [monthlyCounts, setMonthlyCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recordingCash, setRecordingCash] = useState(false);
  // Which bar the admin tapped, by month. Null means "none", and the header
  // falls back to the current count. There is no hover on a phone, so the
  // number has to arrive on tap or not at all.
  const [selectedMonth, setSelectedMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [membersResponse, countsResponse] = await Promise.all([
        apiService.getAllMembers({}),
        // Everything the club can honestly answer for, which today is back to
        // the November 2025 tracking floor. The chart draws the last twelve;
        // asking for all of it means the earlier numbers exist to be quoted
        // without another deploy.
        apiService.getMonthlyMemberCounts(240).catch(() => ({ data: [] }))
      ]);
      setMembers(membersResponse.data || []);
      setMonthlyCounts(countsResponse.data || []);

      // Returns every payment, newest first. A few hundred members means this is
      // a small list; slicing in the browser is fine and avoids a new endpoint.
      const recent = await apiService.get('/membership-payments').catch(() => ({ data: [] }));
      setPayments(Array.isArray(recent.data) ? recent.data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => summarisePayments(members, payments), [members, payments]);

  const chart = useMemo(() => buildChartSeries(monthlyCounts), [monthlyCounts]);

  // Resolved from the month rather than held as an index, so a reload that
  // changes how many months came back cannot leave the selection pointing at a
  // different bar than the one that was tapped.
  const selected = chart.find((point) => point.month === selectedMonth) || null;

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <DSAlert variant="error">{error}</DSAlert>;

  return (
    <div className="membership-payments">
      <DSCard cornerVariant="lg" className="membership-payments__summary">
        <p className="membership-payments__eyebrow">Collected this month</p>
        <p className="membership-payments__amount">{formatMoney(summary.collected)}</p>
        <p className="membership-payments__sub">
          {longMonth(new Date())}, {summary.paidThisMonth} of {summary.expected} members expected to pay
        </p>

        <div className="membership-payments__bar" role="presentation">
          <span style={{ width: `${summary.progress}%` }} />
        </div>

        <div className="membership-payments__methods">
          {METHOD_ORDER.filter((m) => summary.byMethod[m.key]).map((m) => (
            <div key={m.key} className="membership-payments__method">
              <strong>{summary.byMethod[m.key]}</strong>
              <span>{m.label}</span>
            </div>
          ))}

        </div>
      </DSCard>

      {chart.length > 0 && (
        /* The per-month counts live in the header rather than above each bar.
           Twelve figures across a phone is not a chart you read, it is a table
           drawn badly - and a 22px column cannot hold a three-digit number
           anyway. So the header is the one place a number appears: today's
           count by default, and the tapped month's while a bar is selected. */
        <DSCard
          cornerVariant="lg"
          title="Members over time"
          frame="bars"
          headerTone="strong"
          density="compact"
          actions={
            /* aria-live because on tap the number changes somewhere other than
               where the finger is. The buttons name themselves, but nothing
               would otherwise announce that the header had moved. */
            <span className="membership-payments__chart-now" aria-live="polite">
              {selected ? (
                <>
                  <strong>{selected.value}</strong> in {longMonth(selected.month)}
                </>
              ) : (
                <>
                  <strong>{chart[chart.length - 1].value}</strong> now
                </>
              )}
            </span>
          }
        >
          <div className="membership-payments__chart" role="group" aria-label="Members per month">
            {chart.map((point) => (
              /* A real button, not a div with a click handler: it is the only
                 way the bars are reachable by keyboard, and it gives each month
                 an accessible name carrying its count - which is what replaces
                 the summary this chart needed while nothing was tappable. */
              <button
                type="button"
                key={point.month}
                className={[
                  'membership-payments__chart-col',
                  point.month === selectedMonth ? 'is-selected' : ''
                ].filter(Boolean).join(' ')}
                onClick={() => setSelectedMonth(
                  // Tapping the selected bar again clears it, so there is a way
                  // back to "now" that is not a reload.
                  point.month === selectedMonth ? null : point.month
                )}
                aria-pressed={point.month === selectedMonth}
                aria-label={`${longMonth(point.month)}, ${point.value} members`}
              >
                {/* Height in pixels, not a percentage. A percentage on a flex
                    item resolves before the column's other children are laid
                    out, and every bar then shrinks to the same leftover space -
                    which is a chart that draws twelve identical bars. */}
                <span
                  className="membership-payments__chart-bar"
                  style={{ height: `${point.height}px` }}
                />
                {/* Every month keeps a label slot even when it prints nothing,
                    so the bars stay on one baseline: a column whose label is
                    absent is shorter than its neighbours, and flex-end then
                    pushes its bar down past theirs.
                    A selected month always prints its label, so the bar you
                    tapped is named on the axis and not only in the header.
                    aria-hidden because the button's own label already says the
                    month, in full and unabbreviated. */}
                <span className="membership-payments__chart-label" aria-hidden="true">
                  {point.showLabel || point.month === selectedMonth
                    ? shortMonth(point.month)
                    : '\u00a0'}
                </span>
              </button>
            ))}
          </div>
        </DSCard>
      )}

      <DSButton
        tone="primary"
        appearance="solid"
        className="membership-payments__record"
        onClick={() => setRecordingCash(true)}
      >
        Record a cash payment
      </DSButton>

      {/* flush, so each row's separator runs the full width of the card. A body
          that supplied its own padding would stop the rules short of the edge. */}
      <DSCard
        cornerVariant="lg"
        title="Recent payments"
        frame="bars"
        headerTone="strong"
        density="compact"
        flush
      >
        {payments.length === 0 ? (
          <p className="membership-payments__empty">No payments recorded yet.</p>
        ) : (
          <ul className="membership-payments__list">
            {payments.slice(0, 10).map((payment) => (
              <li key={payment.id}>
                <span className="membership-payments__payer">
                  <strong>{payment.first_name ? `${payment.first_name} ${payment.last_name}` : `Member #${payment.user_id}`}</strong>
                  <span className="membership-payments__payer-meta">
                    {longMonth(payment.payment_month)} · {sourceLabel(payment)}
                  </span>
                </span>
                <span className="membership-payments__source">{sourceChip(payment.source_type)}</span>
                <span className="membership-payments__value">{formatMoney(payment.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </DSCard>

      {/* No separate "who is this" step. The sheet starts with the picker,
          which is one interaction instead of two. */}
      {recordingCash && (
        <RecordCashSheet
          onClose={() => setRecordingCash(false)}
          onDone={() => { setRecordingCash(false); load(); }}
        />
      )}
    </div>
  );
}

function sourceLabel(payment) {
  if (payment.source_type === 'cash') {
    return payment.collected_by_username ? `recorded by ${payment.collected_by_username}` : 'recorded by hand';
  }
  return payment.payment_date ? `charged ${shortDate(payment.payment_date)}` : 'charged';
}

function sourceChip(sourceType) {
  switch (sourceType) {
    case 'cash': return 'CASH';
    case 'square_subscription': return 'SUBSCRIPTION';
    case 'square_invoice': return 'INVOICE';
    case 'square_order': return 'SQUARE';
    default: return 'MANUAL';
  }
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function longMonth(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function shortMonth(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}

function shortDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export default MembershipPayments;
